import {
  checkAreas,
  checkEEZ,
  handleAreas,
  handleChoice,
  handleZones,
  resetLayers
} from './areaHelpers';
import { checkInfosAction } from '../mapEntities/zoneInfoActions';
import { ENDPOINTS } from 'other/config';
import { GA } from 'services/analytics';
import {
  getSelectedDate,
  handleLayersInfo,
  TLayerInfoSimple
} from './forecastHelpers';
import { http } from 'services/http';
import {
  initialMapAreas,
  initialMapAreasWithEnvironment,
  initialMapAreasWithWeather
} from './mapOptionsInitialState';
import { isEnvLayer, isWindyLayer } from 'pages/Map/utils/helpers';
import { SettingsService } from 'services/settings';

import {
  EMapArea,
  EMapAreaAction,
  EMapLayer,
  EProtectedZone,
  EUserAuthority
} from 'types';
import { EMapOptionsActions, fetchInfoSet } from './mapOptionsConstants';
import { TAction } from '../../_utils/reducerCreator';
import { TMapOptionsState } from './mapOptionsModel';
import { TState } from '../../appStateModel';

/**
 *
 * @param options
 */
export function updateMapOptionsAction(options: Partial<TMapOptionsState>) {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;
    const update = {
      ...mapOptions,
      ...options
    };
    const action: TAction<TMapOptionsState, EMapOptionsActions> = {
      type: EMapOptionsActions.MAP_SET_OPTIONS,
      payload: update
    };

    SettingsService.writeSettings({ [SettingsService.MAP_SETTINGS]: update });
    dispatch(action);

    if ('layer' in options) {
      GA.reportLayerChange(options.layer);
    }
  };
}

/**
 *
 */
export function checkMapOptions() {
  return (dispatch, getState) => {
    const { mapOptions, session } = getState() as TState;
    const permissions = session.user?.userInfo?.authorities || [];

    if (
      !permissions.includes(EUserAuthority.VIEW_MAP_ICE_LAYER) &&
      mapOptions.layer === EMapLayer.ICE
    ) {
      dispatch(updateMapOptionsAction({ layer: null }));
    }
  };
}

/**
 *
 */
export function changeMapLayer(layer: EMapLayer) {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;
    const update = checkAreas(mapOptions.mapAreas, layer);

    if (update.feature === EMapArea.DEPTH_CURVES_COLOUR) {
      update.feature = null;
      update.top = [...update.top, EMapArea.DEPTH_CURVES_BW] as any;
    }

    if (update.base === EMapArea.SEABED_COLOUR) {
      update.base = EMapArea.SEABED_BW;
    }

    dispatch(
      updateMapOptionsAction({
        layer: layer,
        mapAreas: update
      })
    );
  };
}

/**
 *
 */
export function changeMapAreaAction(area: EMapArea, action: EMapAreaAction) {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;
    const mapAreas = handleAreas(mapOptions.mapAreas, area, action);

    const update = area === EMapArea.EEZ_LINES ? mapAreas : checkEEZ(mapAreas);
    const checked = checkAreas(update, mapOptions.layer);

    dispatch(
      updateMapOptionsAction({
        mapAreaChoice: handleChoice(area, action, mapOptions),
        mapAreas: checked
      })
    );
  };
}

/**
 *
 */
export function manageProtectedAreas(
  zones: Array<EProtectedZone>,
  action: EMapAreaAction
) {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;

    dispatch(
      updateMapOptionsAction({
        mapAreas: handleZones(mapOptions.mapAreas, zones, action)
      })
    );

    action === EMapAreaAction.ADD || dispatch(checkInfosAction());
  };
}

/**
 *
 */
export function resetMapAreasAction() {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;
    let update = initialMapAreas;

    if (isWindyLayer(mapOptions.layer)) {
      update = initialMapAreasWithWeather;
    } else if (isEnvLayer(mapOptions.layer)) {
      update = initialMapAreasWithEnvironment;
    }

    dispatch(updateMapOptionsAction({ mapAreas: update }));
    dispatch(checkInfosAction(true));
  };
}

/**
 *
 */
export function resetLayersAction() {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;

    dispatch(
      updateMapOptionsAction({
        layer: null,
        mapAreas: resetLayers(mapOptions)
      })
    );
  };
}

/**
 *
 */
export function fetchLayerInfoAction() {
  return (dispatch, getState) => {
    const { mapOptions } = getState() as TState;
    if (Object.keys(mapOptions.layerInfo).length > 0 || mapOptions.isPending) {
      return;
    }

    dispatch(fetchInfoSet.request());

    http
      .send(ENDPOINTS.LAYER_INFO)
      .then((r: Record<number, TLayerInfoSimple>) => {
        const layerInfo = handleLayersInfo(r);

        dispatch(
          fetchInfoSet.success({
            layerDateLabel: getSelectedDate(
              layerInfo[mapOptions.layer].dateList
            ),
            layerInfo: layerInfo
          })
        );
      })
      .catch((e) => dispatch(fetchInfoSet.error(e)));
  };
}
